import { X } from 'lucide-react'
import { useRef, useCallback, useEffect } from 'react'

/**
 * Panel deslizado desde abajo para móvil — reemplaza dropdowns `absolute`
 * de ancho fijo y el drag-por-píxeles de DraggablePanel, que no caben ni
 * se usan bien con el dedo en una pantalla estrecha.
 *
 * UX móvil (PWA): handle visible, drag-to-dismiss arrastrando desde la
 * cabecera/handle, Escape para cerrar, altura máx. 90dvh y respeto de la
 * safe-area inferior (home bar / barras de gestos). El drag solo se
 * engancha en la cabecera: el contenido scrollea con normalidad.
 */
export default function BottomSheet({ title, onClose, children, loading = false, headerExtra = null }) {
  const panelRef = useRef(null)
  const dragRef = useRef({ dragging: false, startY: 0, currentY: 0 })

  // Ghost click de iOS Safari: cuando un tap abre el sheet, iOS puede
  // re-disparar el click (~300ms después) sobre el elemento que quede bajo
  // el dedo (backdrop, X) y cerrarlo al instante — el usuario nunca llega a
  // verlo. Se ignoran los cierres durante los primeros 400ms tras montar.
  const mountedAtRef = useRef(0)
  useEffect(() => { mountedAtRef.current = Date.now() }, [])
  const safeClose = useCallback(() => {
    if (Date.now() - mountedAtRef.current < 400) return
    onClose()
  }, [onClose])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') safeClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [safeClose])

  const onDragStart = useCallback((e) => {
    dragRef.current = { dragging: true, startY: e.touches[0].clientY, currentY: 0 }
    if (panelRef.current) panelRef.current.style.transition = 'none'
  }, [])

  const onDragMove = useCallback((e) => {
    const d = dragRef.current
    if (!d.dragging) return
    // Solo hacia abajo; hacia arriba el sheet ya está en su tope
    const dy = Math.max(0, e.touches[0].clientY - d.startY)
    d.currentY = dy
    if (panelRef.current) panelRef.current.style.transform = `translateY(${dy}px)`
  }, [])

  const onDragEnd = useCallback(() => {
    const d = dragRef.current
    if (!d.dragging) return
    d.dragging = false
    const panel = panelRef.current
    if (!panel) return
    const threshold = Math.min(120, panel.offsetHeight * 0.3)
    if (d.currentY > threshold) {
      onClose() // se desmonta; no hace falta animación de salida
    } else {
      panel.style.transition = 'transform 180ms ease-out'
      panel.style.transform = ''
    }
  }, [onClose])

  return (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end" style={{ background: 'rgba(4, 9, 15, 0.4)' }}>
      <div className="absolute inset-0" onClick={safeClose} />
      <div
        ref={panelRef}
        data-bottom-sheet
        className="relative flex flex-col max-h-[90dvh] rounded-t-2xl border-t shadow-2xl"
        style={{
          borderColor: 'var(--hair)',
          background: 'var(--green-panel)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {/* Zona de arrastre: handle + cabecera */}
        <div
          data-sheet-drag
          className="flex-shrink-0 touch-none select-none"
          onTouchStart={onDragStart}
          onTouchMove={onDragMove}
          onTouchEnd={onDragEnd}
        >
          <div className="flex justify-center pt-2 pb-1">
            <div className="w-10 h-1 rounded-full" style={{ background: 'var(--hair-bright)' }} />
          </div>
          <div className="flex items-center justify-between px-4 pb-2.5 border-b" style={{ borderColor: 'var(--hair)' }}>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold" style={{ color: 'var(--ivory)' }}>{title}</span>
              {loading && (
                <span className="w-3 h-3 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--gold)' }} />
              )}
            </div>
            <div className="flex items-center gap-1">
              {headerExtra}
              <button
                type="button"
                onClick={safeClose}
                className="p-2.5 -m-1.5 rounded-lg transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center"
                style={{ color: 'var(--txt-dim)' }}
                aria-label="Cerrar"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {children}
        </div>
      </div>
    </div>
  )
}
